import React, { ReactElement } from "react"
import {
	AlertColor,
	Box,
	Button,
	Divider,
	IconButton,
	Link as MuiLink,
	Popover,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material"

import { Link } from "react-router-dom"
import {
	t,
	Trans,
} from "@lingui/macro"

import Icon from "@mdi/react"
import {
	mdiChevronDoubleRight,
	mdiChevronDown,
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
	mdiHeartPlusOutline,
	mdiMap,
} from "@mdi/js"

import {
	AssetProvenience,
	AssetWithImage,
	CreateSpecificWorkflow,
	NewStepRequest,
	WorkflowObjectType,
} from "models"

import noImage from "components/dashboard/dashboard-map/dashboard-map-stores/no-image-found-masema.png"
import withUseRouter, { UseRouterParams } from "services/router"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import { AssetService } from "services/asset.service"
import { AccountServices } from "services/account.services"
import { DashboardMapAttractors } from "components/dashboard/dashboard-map-attractors/dashboard-map-attractors"
import { lightTheme } from "commons/themes"
import { MapModal } from "components/asset/asset-details/asset-modals/map-modal"
import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"
import TemplateSelectionModal from "components/commons/template-selection-modal"
import { WorkflowService } from "services/workflow.service"
import { SbSnackbar } from "components/commons/sb-snackbar"
import { NewStep } from "components/workflow/modals"
import { ActionsServices } from "services/actions.services"

export interface DashboardAssetListItemProps extends UseRouterParams {
	asset: AssetWithImage
	likes: string[]
	refreshLikes?: () => void
}
export interface DashboardAssetListItemState extends MatesComponentState {
	showAttractorsDetail: boolean
	likeAsset: boolean
	openMap: boolean
	openActions: boolean
	openWorkflow: boolean
	openReminder: boolean
	snackbarData: {
		openNotification: boolean,
		notificationMessage: string,
		severity: AlertColor
	}
}
class DashboardAssetListItem extends React.Component<DashboardAssetListItemProps, DashboardAssetListItemState> {
	private readonly attractorsDetailRef: React.RefObject<any> = React.createRef()
	private readonly actionButtonRef: React.RefObject<any> = React.createRef()

	private readonly matesComponent: MatesComponent<DashboardAssetListItemProps, DashboardAssetListItemState> = new MatesComponent(this)

	constructor(props: DashboardAssetListItemProps) {
		super(props)

		this.state = {
			...this.matesComponent.setupState(),
			showAttractorsDetail: false,
			likeAsset: this.props.likes.some((assetId) => assetId === this.props.asset.asset.id),
			openMap: false,
			openActions: false,
			openWorkflow: false,
			openReminder: false,
			snackbarData: {
				openNotification: false,
				notificationMessage: "",
				severity: "info"
			}
		}
	}

	componentDidUpdate(prevProps: Readonly<DashboardAssetListItemProps>) {
		if (prevProps.likes !== this.props.likes) {
			this.setState(() => ({
				likeAsset: this.props.likes.some((assetId) => assetId === this.props.asset.asset.id),

			}))
		}
	}

	private toggleFavourite = () => {
		this.setState(() => ({ loading: true }))
		if (!this.state.likeAsset) {
			this.addToFavourite()
		} else {
			this.removeFromFavourite()
		}
	}

	private addToFavourite = () => {
		AssetService.addToFavourite(this.props.asset.asset.id, this.addToFavouriteCallback, this.matesComponent.onException)
	}

	private addToFavouriteCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		if(this.props.refreshLikes) {
			this.props.refreshLikes() 
			this.setState(() => ({
				loading: false
			}))
		}else
			AccountServices.getAssetLikes(this.getAssetLikesCallback, this.matesComponent.onException)
	}

	private removeFromFavourite = () => {
		AssetService.removeFromFavourite(this.props.asset.asset.id, this.removeFromFavouriteCallback, this.matesComponent.onException)
	}

	private removeFromFavouriteCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		if(this.props.refreshLikes) {
			this.props.refreshLikes() 
			this.setState(() => ({
				loading: false
			}))
		}else
		AccountServices.getAssetLikes(this.getAssetLikesCallback, this.matesComponent.onException)
	}

	private getAssetLikesCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			likeAsset: !!response.data.filter((assetId: string) => assetId === this.props.asset.asset.id)[0],
			loading: false
		}))
	}

	private openAttractorsDetail = (): void => {
		this.setState(() => ({
			showAttractorsDetail: true
		}))
	}

	private closeAttractorsDetail = (): void => {
		this.setState(() => ({
			showAttractorsDetail: false,
		}))
	}

	private getLogo = (): string => {
		switch (this.props.asset.asset.provenience) {
			case AssetProvenience.IMMOBILIARE:
				return "/asset-provenience-logo/immobiliare.png"
			case AssetProvenience.CATASTO:
				return "/asset-provenience-logo/agenzia-del-territorio.png"
			case AssetProvenience.SELECTED_BY_STOREBOOK:
				return "/asset-provenience-logo/no-image-found-masema.png"
			case AssetProvenience.SUBITO:
				return "/asset-provenience-logo/subito.png"
			case AssetProvenience.IDEALISTA:
				return "/asset-provenience-logo/idealista.png"
			case AssetProvenience.FRONTEND:
				return ""
			default:
				return ""
		}
	}

	private getCompatibility = (): ReactElement => {
		if (this.props.asset.asset.matches && this.props.asset.asset.matches.length > 0) {
			const value = this.props.asset.asset.matches[0].compatibility || 0
			let emoticon: ReactElement = (
				<Icon path={mdiEmoticonExcited} size={1} color={lightTheme.palette.info.main} />
			)

			if (value <= 66) {
				emoticon = <Icon path={mdiEmoticonNeutral} size={1} color={lightTheme.palette.info.main} />
			}

			if (value <= 33) {
				emoticon = <Icon path={mdiEmoticonSad} size={1} color={lightTheme.palette.info.main} />
			}

			return emoticon
		}
		return <></>
	}

	private openMap = () => {
		this.setState(() => ({
			openMap: true
		}))
	}

	private closeMapModal = () => {
		this.setState(() => ({
			openMap: false
		}))
	}

	private createWorkflow = (templateId: string) => {
		if (this.props.asset) {
			const request: CreateSpecificWorkflow = {
				objectId: this.props.asset.asset.id,
				objectType: WorkflowObjectType.ASSET,
				workflowTemplateId: templateId
			}
			WorkflowService.createWorkflowFromSpecificObject(request, this.createWorkflowCallback, this.matesComponent.onError)
		}
	}

	private createWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.openSnackbar(
				t({ id: "asset-form.create-workflow.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			this.matesComponent.onError(response)
			return
		}
		this.openSnackbar(
			t({ id: "asset-form.create-workflow.success", message: "Workflow creato con successo." }),
			"success"
		)
	}

	private createReminder = (request: NewStepRequest) => {
		ActionsServices.createNewAction(request, this.createReminderCallback, this.matesComponent.onError)
	}

	private createReminderCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.openSnackbar(
				t({ id: "asset-form.create-reminder.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			this.setState(() => ({
				openReminder: false
			}))
			return
		}

		this.openSnackbar(
			t({ id: "asset-form.create-reminder.success", message: "Promemoria creato con successo." }),
			"success"
		)
		this.setState(() => ({
			openReminder: false
		}))
	}


	private openSnackbar = (message: string, severity: AlertColor) => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity: severity
			}
		}))
	}

	private closeSnackbar = () => {
		this.setState((prevState) => ({
			snackbarData: {
				...prevState.snackbarData,
				openNotification: false
			}
		}))
	}

	render() {
		const { asset } = this.props.asset
		const characteristics = asset.characteristics
		const emptyValue = "ND"

		let image: string | undefined = this.props.asset.image
		let linkUrl: string = `/asset/${asset.id}`

		if ((image?.length || 0) === 0) {
			image = noImage
		}

		return (
			<>
				<Loading show={this.state.loading} />
				<Stack
					direction="row"
					sx={{
						fontSize: "8px",
						height: "130px"
					}}
				>
					<Box
						component="img"
						sx={{
							height: "100%",
							borderRadius: "10px",
							aspectRatio: 16 / 10,
							background: "#f9fafb",
							mr: 2
						}}
						alt="Immagine Immobile"
						src={image}
					/>
					<Stack
						direction="column"
						sx={{ backgroundColor: "#f9fafb", height: "100%", borderRadius: "10px", flex: 1, mb: 2, px: 2 }}>
						<Stack
							direction="row"
							sx={{
								justifyContent: "space-between",
								alignItems: "center",
								px: 1,
							}}
							spacing={2}>
							<Typography sx={{ minWidth: "50%" }}>
								{asset.address}
							</Typography>
							<Box
								component={"img"}
								src={this.getLogo()}
								sx={{
									height: "48px",
									display: "block",
									margin: "0 auto",
									padding: 1,
								}}
							/>
						</Stack>
						<Stack
							direction="row"
							spacing={1}
							divider={<Divider orientation="vertical" flexItem variant="middle" />}
							sx={{ px: 1, pb: "4px", justifyContent: "space-around", textAlign: "center", mt: .75, }}>
							<Box sx={{ width: "35px" }}>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-gla">mq</Trans></Typography>
								<Typography fontSize="0.8rem">{characteristics?.gla ?? emptyValue}</Typography>
							</Box>
							<Box>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-divisible">divisibile</Trans></Typography>
								<Typography fontSize="0.8rem">
									{characteristics?.divisible === undefined || characteristics.divisible === null
										? emptyValue
										: characteristics.divisible ? "SI" : "NO"}
								</Typography>
							</Box>
							<Box>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-floors">piani</Trans></Typography>
								<Typography fontSize="0.8rem">{characteristics?.floors?.length ?? emptyValue}</Typography>
							</Box>
							<Box>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-levels">livelli</Trans></Typography>
								<Typography fontSize="0.8rem">{characteristics?.floors?.length ?? emptyValue}</Typography>
							</Box>
							<Box>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-windows">vetrine</Trans></Typography>
								<Typography fontSize="0.8rem">{characteristics?.shopWindows ?? emptyValue}</Typography>
							</Box>
							<Box>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-price">prezzo</Trans></Typography>
								<Typography fontSize="0.8rem">{characteristics?.price ?? emptyValue}</Typography>
							</Box>
							<Box
								onClick={characteristics ? this.openAttractorsDetail : undefined}
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									position: "relative",
									cursor: characteristics ? "pointer" : "default",
								}}
							>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-attractors">distanze attrattori</Trans></Typography>
								{characteristics && <Box component={Icon} path={mdiChevronDown} size={"1rem"} sx={{ position: "absolute", bottom: "-2px" }} />}
							</Box>
							<Tooltip title={
								this.props.asset.asset.matches ?
									this.props.asset.asset.matches.map(match => match.brand.name).join("\n") :
									<Trans id={"dms-compatibility-not-found"}>Compatibilità non trovata</Trans>
							}
							>
								<Box>
									<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-compatibility">compatibilità</Trans></Typography>
									{this.getCompatibility()}
								</Box>
							</Tooltip>

							<Box>
								<Typography fontWeight="bold" fontSize="0.8rem"><Trans id="dms-proposal">proposte</Trans></Typography>
								<Typography fontSize="0.8rem">{asset.matches?.length || 0}</Typography>
							</Box>
							<Box
								sx={{
									display: "flex",
									gap: 2,
									alignItems: "center"
								}}
							>
								<IconButton
									sx={{
										color: this.state.likeAsset ? "secondary.main" : "inherit",
										backgroundColor: "transparent",
									}}
									aria-label="Add to favorites"
									onClick={this.toggleFavourite}
									disableFocusRipple
									disableRipple>
									<Icon path={mdiHeartPlusOutline} size={1} />
								</IconButton>
								{
									this.props.asset && this.props.asset.asset.location &&
									<Tooltip title={t({
										id: "af-asset-map-button",
										message: "Apri nella mappa",
									})}>
										<IconButton onClick={this.openMap}>
											<Icon path={mdiMap} size={1} />
										</IconButton>
									</Tooltip>
								}
								<Button
									variant={"outlined"}
									color={"info"}
									size="small"
									startIcon={<Icon path={mdiChevronDoubleRight}
										size={1}
										color={lightTheme.palette.info.main} />}
									sx={{
										px: 1,
										fontSize: "0.8rem",
										height: "fit-content"
									}}
									ref={this.actionButtonRef}
									onClick={() => this.setState(() => ({ openActions: true }))}
								>
									<Trans id={"dms-fast-actions"}>Azioni rapide</Trans>
								</Button>
							</Box>
						</Stack>

						<Popover
							anchorEl={this.attractorsDetailRef.current}
							open={this.state.showAttractorsDetail}
							onClose={this.closeAttractorsDetail}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "left",
							}}
							slotProps={{
								paper: {
									sx: {
										width: this.attractorsDetailRef.current?.offsetWidth ?? 0,
										p: 2,
										overflow: "hidden",
									},
								},
							}}
						>
							{characteristics && <DashboardMapAttractors characteristics={characteristics} />}
						</Popover>

						<Divider ref={this.attractorsDetailRef} />

						<MuiLink
							color="secondary"
							component={Link}
							to={linkUrl}
							fontSize="12px"
							padding={1}
							sx={{
								textAlign: "center",
								textDecoration: "none",
							}}
						>Vedi scheda completa</MuiLink>
					</Stack>
					{
						this.state.openMap && this.props.asset &&
						<MapModal closeMapModal={this.closeMapModal} asset={this.props.asset} />
					}
					<Popover
						anchorEl={this.actionButtonRef.current}
						open={this.state.openActions}
						onClose={() => this.setState(() => ({ openActions: false }))}
						anchorOrigin={{
							vertical: "center",
							horizontal: "left",
						}}
						transformOrigin={{
							vertical: "center",
							horizontal: "right",
						}}
						slotProps={{
							paper: {
								sx: {
									p: 2,
									overflow: "hidden",
									width: "200px"
								},
							},
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "start",
								gap: 2,
							}}
						>
							<Box
								sx={{
									cursor: "pointer"
								}}
								onClick={() => this.props.navigate(`/match-proposal/asset/${this.props.asset.asset.id}`)}
							>
								<Trans id={"dms-fast-actions.sent-match"}>Invia proposta</Trans>
							</Box>
							<Box
								sx={{
									cursor: "pointer"
								}}
								onClick={() => this.setState(() => ({ openReminder: true, openActions: false }))}
							>
								<Trans id={"dms-fast-actions.reminder"}>Crea promemoria</Trans>
							</Box>
							<Box
								sx={{
									cursor: "pointer"
								}}
								onClick={() => this.setState(() => ({ openWorkflow: true, openActions: false }))}
							>
								<Trans id={"dms-fast-actions.workflow"}>Scatena workflow</Trans>
							</Box>
						</Box>
					</Popover>

					{
						this.state.openWorkflow &&
						<TemplateSelectionModal
							templateType={WorkflowObjectType.ASSET}
							onClose={() => this.setState(() => ({ openWorkflow: false }))}
							submit={this.createWorkflow}
						/>
					}
					{
						this.state.openReminder &&
						<NewStep
							openDialog
							onClose={() => this.setState(() => ({ openReminder: false }))}
							submit={this.createReminder}
							action
							create
						/>
					}

				</Stack>
				<SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.closeSnackbar} />
			</>
		)
	}
}

export default withUseRouter(DashboardAssetListItem)
